// ============================================================================
// TTS VOICES SERVICE (Frontend)
// Managed ElevenLabs voice list used by avatar voice pickers and the admin page.
// ============================================================================

import { api } from './api';

export type TtsVoiceUsage = 'avatar' | 'narration';

export interface TtsVoice {
  voiceId: string;
  name: string;
  label: string;
  accent?: string;
  gender?: string;
  character?: string;
  wpm?: number;
  notes?: string;
  usage: TtsVoiceUsage[];
  enabled: boolean;
  source: 'app' | 'skill' | 'imported' | 'admin';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TtsVoiceInput {
  voiceId: string;
  name?: string;
  accent?: string;
  gender?: string;
  character?: string;
  wpm?: number | null;
  notes?: string;
  usage?: TtsVoiceUsage[];
  enabled?: boolean;
}

export type TtsVoicePatch = Partial<Omit<TtsVoiceInput, 'voiceId'>>;

export const ttsVoicesService = {
  async list(usage?: TtsVoiceUsage, all?: boolean): Promise<TtsVoice[]> {
    const params: Record<string, string> = {};
    if (usage) params.usage = usage;
    if (all) params.all = 'true';
    const { data } = await api.get<{ voices: TtsVoice[] }>('/api/tts-voices', { params });
    return data.voices;
  },

  async preview(voiceId: string): Promise<string> {
    const { data } = await api.get<{ url: string }>(`/api/tts-voices/${encodeURIComponent(voiceId)}/preview`);
    return data.url;
  },

  async create(input: TtsVoiceInput): Promise<TtsVoice> {
    const { data } = await api.post<{ voice: TtsVoice }>('/api/tts-voices', input);
    return data.voice;
  },

  async update(voiceId: string, patch: TtsVoicePatch): Promise<TtsVoice> {
    const { data } = await api.put<{ voice: TtsVoice }>(`/api/tts-voices/${encodeURIComponent(voiceId)}`, patch);
    return data.voice;
  },
};

const listCache = new Map<string, Promise<TtsVoice[]>>();

export function listVoicesCached(usage: TtsVoiceUsage = 'avatar'): Promise<TtsVoice[]> {
  let pending = listCache.get(usage);
  if (!pending) {
    pending = ttsVoicesService.list(usage).catch(err => {
      listCache.delete(usage);
      throw err;
    });
    listCache.set(usage, pending);
  }
  return pending;
}

export function clearVoiceListCache(): void {
  listCache.clear();
}

export function apiErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.message || fallback;
}
